import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Match, MatchStatus } from '@/types/ai';
import { estimateMatchingCost, formatCents } from '@/lib/ai-cost';

export function useAIMatches(filters?: { status?: MatchStatus; batchId?: string }) {
  return useQuery({
    queryKey: ['ai-matches', filters],
    queryFn: async () => {
      let query = supabase
        .from('matches')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.batchId) {
        query = query.eq('batch_id', filters.batchId);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;

      // Fetch profile info for matched users
      const userIds = new Set<string>();
      (data || []).forEach((m: Match) => {
        userIds.add(m.user_id_1);
        userIds.add(m.user_id_2);
      });

      let profiles: Record<string, { full_name: string; avatar_url: string | null; company: string | null; role: string | null; city_cohort: string }> = {};

      if (userIds.size > 0) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url, company, role, city_cohort')
          .in('user_id', Array.from(userIds));

        if (profileData) {
          profiles = Object.fromEntries(profileData.map((p) => [p.user_id, p]));
        }
      }

      return { matches: (data || []) as Match[], profiles };
    },
  });
}

export function useRunAIMatching() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { cohort_filter?: string; member_ids?: string[]; custom_instructions?: string; max_matches_override?: number }) => {
      const { data, error } = await supabase.functions.invoke('ai-match', {
        body: params,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-matches'] });
    },
  });
}

export function useUpdateMatchStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      matchId,
      status,
      declineReason,
    }: {
      matchId: string;
      status: MatchStatus;
      declineReason?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const updates: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'approved') {
        updates.approved_by = user?.id;
        updates.approved_at = new Date().toISOString();
      }
      if (status === 'introduced') {
        updates.introduced_at = new Date().toISOString();
      }
      if (status === 'declined' && declineReason) {
        updates.decline_reason = declineReason;
      }

      const { error } = await supabase
        .from('matches')
        .update(updates)
        .eq('id', matchId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-matches'] });
    },
  });
}

export function useMatchCostEstimate(memberCount: number, model: string) {
  const estimate = estimateMatchingCost(memberCount, model);
  return {
    ...estimate,
    formatted: formatCents(estimate.estimatedCostCents),
  };
}
